import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Home, Building2, Users, Video, BarChart3, CreditCard,
  Settings, BookOpen, HelpCircle, FileText,
  Calculator, ChevronLeft, X, ChevronsUpDown, Puzzle, MessagesSquare,
} from 'lucide-react';
import Badge from '../ui/Badge';
import OrgSwitcher from './OrgSwitcher';
import { usePreferences } from '../../contexts/PreferencesContext';

// Visual sectioning labels — picked once so the order matches the
// groupNavigationItems() output (workspace → manage → account).
const GROUP_LABELS = ['Workspace', 'Manage', 'Account'];

// Splits the (untouched) navigation array into three visual groups
// without mutating it. The data layer still owns ordering and content.
//   - workspace: first item only (the dashboard)
//   - account: trailing tail of items whose path ends in /settings or /help
//   - manage: everything in between
// Determined by path suffix so future role tweaks don't drift the layout.
const groupNavigationItems = (items) => {
  if (!items || items.length === 0) return [[], [], []];
  const workspace = [items[0]];

  let tailStart = items.length;
  for (let i = items.length - 1; i >= 1; i -= 1) {
    const path = items[i].path || '';
    if (path.endsWith('/settings') || path.endsWith('/help')) {
      tailStart = i;
    } else {
      break;
    }
  }

  return [workspace, items.slice(1, tailStart), items.slice(tailStart)];
};

// Avatar gradient palette + deterministic hash. Same name → same gradient,
// no per-render flicker. Polynomial rolling hash is fine; collisions just
// mean two users share a colorway.
const AVATAR_GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-purple-500 to-fuchsia-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
];
const userGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = usePreferences();
  const [user, setUser] = useState(null);

  // Stagger nav items only on the first paint — subsequent renders (route
  // changes, role-driven nav changes, drawer open/close) shouldn't replay
  // the entrance animation. useRef avoids the re-render that useState
  // would trigger.
  const mountedRef = useRef(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  // Flip the mounted flag once the first paint has happened. We read it
  // synchronously inside render to decide whether to skip the entrance,
  // so it must be a ref (not state) — otherwise the very render that
  // sets it would also be the one that animates.
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const getNavigationItems = () => {
    if (!user) return [];

    const baseItems = {
      'super-admin': [
        { name: t('dashboard'), icon: Home, path: '/admin/dashboard' },
        { name: t('organizations'), icon: Building2, path: '/admin/organizations' },
        { name: t('users'), icon: Users, path: '/admin/users' },
        { name: t('sessions'), icon: Video, path: '/admin/sessions' },
        { name: t('analytics'), icon: BarChart3, path: '/admin/analytics' },
        { name: t('billing'), icon: CreditCard, path: '/admin/billing' },
        // Literal label: no translation key exists (t() would echo the key).
        { name: 'Micro simulations', icon: MessagesSquare, path: '/admin/micro-sims' },
        { name: t('settings'), icon: Settings, path: '/admin/settings' },
      ],
      client: [
        { name: t('dashboard'), icon: Home, path: '/client/dashboard' },
        { name: t('sessions'), icon: Video, path: '/client/sessions' },
        { name: t('trainers'), icon: Users, path: '/client/trainers' },
        { name: t('analytics'), icon: BarChart3, path: '/client/analytics' },
        { name: t('reports'), icon: FileText, path: '/client/reports' },
        { name: t('workbooks'), icon: BookOpen, path: '/client/workbooks' },
        { name: t('subscription'), icon: CreditCard, path: '/client/subscription', badge: user?.primaryOrganization?.subscriptionTier || user?.organization?.subscriptionTier },
        { name: t('terminology'), icon: Calculator, path: '/client/terminology' },
        { name: t('help'), icon: HelpCircle, path: '/client/help' },
        { name: t('settings'), icon: Settings, path: '/client/settings' },
      ],
      trainer: [
        { name: t('dashboard'), icon: Home, path: '/trainer/dashboard' },
        { name: t('mySessions'), icon: Video, path: '/trainer/sessions' },
        // Simulations is granted per trainer by a super-admin, so it is absent by
        // default rather than disabled — a menu item that only ever errors is worse
        // than no menu item. The API enforces the same flag independently, so this
        // is presentation only.
        // Literal label, not t(): the translation map has no 'simulations' key and
        // t() falls back to returning the key itself, which would render lowercase.
        // The entry opens a choice screen (team simulation / micro simulation) only when
        // the trainer has both; with just one, it links straight to that page so the
        // menu item stays highlighted there (isActive is an exact match).
        ...(user?.features?.simulations || user?.features?.microSims
          ? [{
              name: 'Simulations',
              icon: Puzzle,
              path: user?.features?.simulations && user?.features?.microSims
                ? '/trainer/simulations'
                : user?.features?.simulations ? '/trainer/simulations/team' : '/trainer/micro-sims',
            }]
          : []),
        { name: t('workbooks'), icon: BookOpen, path: '/trainer/workbooks' },
        { name: t('performance'), icon: BarChart3, path: '/trainer/performance' },
        // Solo trainers (individual org type) get the extra session-level
        // Analytics view (mirrors client analytics) plus Billing.
        ...(user.primaryOrganization?.type === 'individual' || user.organization?.type === 'individual'
          ? [
              { name: t('analytics') || 'Analytics', icon: BarChart3, path: '/trainer/analytics' },
              { name: t('billing') || 'Billing', icon: CreditCard, path: '/trainer/billing' },
            ]
          : []),
        { name: t('help'), icon: HelpCircle, path: '/trainer/help' },
        { name: t('settings'), icon: Settings, path: '/trainer/settings' },
      ],
    };

    return baseItems[user.role] || [];
  };

  const navigationItems = getNavigationItems();
  const isActive = (path) => location.pathname === path;

  // ────── Nav item ──────
  // The active row gets two visual signals:
  //   (a) a soft white "raised" surface with ring + shadow (like Linear)
  //   (b) a 1px gradient accent bar pinned to the left edge
  // In collapsed mode we drop (a) and (b) for a simpler filled-square
  // since there's no room and the icon needs to read as a target.
  const NavItem = ({ item, isMobile = false }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const showLabel = !collapsed || isMobile;

    return (
      <button
        onClick={() => {
          navigate(item.path);
          if (isMobile && onMobileClose) onMobileClose();
        }}
        className={`group relative w-full flex items-center gap-3 rounded-xl
          transition-[background-color,color,box-shadow,transform] duration-200 ease-out
          active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40
          ${showLabel ? 'px-3 py-2.5' : 'p-2.5 h-10 w-10 mx-auto justify-center'}
          ${active
            ? showLabel
              ? 'bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50 font-semibold ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
              : 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400'
            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/70 dark:hover:bg-neutral-900/60 hover:text-neutral-900 dark:hover:text-neutral-100'
          }`}
        title={collapsed && !isMobile ? item.name : ''}
      >
        {/* Active gradient accent bar — expanded only */}
        {active && showLabel && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700"
          />
        )}

        {/* Icon + collapsed-mode badge dot */}
        <span className="relative flex-shrink-0">
          <Icon
            size={20}
            strokeWidth={active ? 2 : 1.75}
            className={active && showLabel ? 'text-primary-600 dark:text-primary-400' : ''}
          />
          {collapsed && !isMobile && item.badge && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary-500 ring-2 ring-white dark:ring-neutral-950"
            />
          )}
        </span>

        {/* Label */}
        <AnimatePresence initial={false}>
          {showLabel && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.15 }}
              className="text-[13px] font-medium tracking-tight whitespace-nowrap overflow-hidden"
            >
              {item.name}
            </motion.span>
          )}
        </AnimatePresence>

        {/* Badge — expanded only */}
        {showLabel && item.badge && (
          <Badge variant="primary" size="sm" className="ml-auto">
            {item.badge}
          </Badge>
        )}

        {/* Collapsed flyout tooltip — replaces the native title tooltip with
            a styled chip. Native title attr is kept as a fallback (above)
            for screen readers / no-JS users. */}
        {collapsed && !isMobile && (
          <span
            role="tooltip"
            className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-[0_4px_16px_-4px_rgba(16,24,40,0.18)] whitespace-nowrap"
          >
            {item.name}
          </span>
        )}
      </button>
    );
  };

  // ────── Section header (expanded mode) ──────
  const SectionHeader = ({ label, isFirst }) => (
    <h3
      className={`text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400 dark:text-neutral-500 px-3 mb-1.5 select-none ${isFirst ? 'mt-1' : 'mt-4'}`}
    >
      {label}
    </h3>
  );

  // ────── Render the grouped nav list ──────
  // Used by both the desktop aside and the mobile drawer. Stagger only
  // fires on first paint; subsequent renders (route changes, drawer opens)
  // skip the initial offset.
  const renderNavList = (isMobile) => {
    const groups = groupNavigationItems(navigationItems);
    const out = [];
    let absoluteIndex = 0;

    groups.forEach((group, gi) => {
      if (group.length === 0) return;

      if (!collapsed || isMobile) {
        // Expanded: small uppercase section header
        out.push(
          <li key={`hdr-${gi}`}>
            <SectionHeader label={GROUP_LABELS[gi]} isFirst={gi === 0} />
          </li>
        );
      } else if (gi > 0) {
        // Collapsed: hairline divider between groups (no labels — no room)
        out.push(
          <li key={`div-${gi}`} aria-hidden="true">
            <div className="mx-3 my-2 border-t border-neutral-100 dark:border-neutral-900" />
          </li>
        );
      }

      group.forEach((item) => {
        const idx = absoluteIndex;
        absoluteIndex += 1;
        const skipStagger = mountedRef.current || prefersReducedMotion;
        out.push(
          <motion.li
            key={item.path}
            initial={skipStagger ? false : { opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={skipStagger ? { duration: 0 } : { delay: idx * 0.025, duration: 0.18 }}
          >
            <NavItem item={item} isMobile={isMobile} />
          </motion.li>
        );
      });
    });

    return out;
  };

  // ────── User footer card ──────
  const UserFooter = ({ isMobile = false }) => {
    if (!user) return null;
    const name = user.fullName || user.email || 'User';
    const initial = (name?.charAt(0) || 'U').toUpperCase();
    const gradientCls = userGradient(name);

    // Collapsed (desktop) → just a centered avatar, no card.
    if (collapsed && !isMobile) {
      return (
        <div className="px-3 mb-3 mt-1">
          <div className={`relative w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${gradientCls} flex items-center justify-center text-white font-semibold text-sm shadow-[inset_0_-2px_4px_rgba(0,0,0,0.08)]`}>
            {initial}
            <span aria-hidden="true" className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-neutral-950" />
          </div>
        </div>
      );
    }

    return (
      <div className="mx-3 mb-3 mt-1">
        <div className="group rounded-xl bg-neutral-50/80 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-200/60 dark:ring-neutral-800 p-2.5 flex items-center gap-2.5">
          <div className={`relative w-9 h-9 rounded-xl bg-gradient-to-br ${gradientCls} flex items-center justify-center text-white font-semibold text-sm shadow-[inset_0_-2px_4px_rgba(0,0,0,0.08)] flex-shrink-0`}>
            {initial}
            <span aria-hidden="true" className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-neutral-950" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{name}</p>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 capitalize truncate">
              {user.role?.replace('_', ' ')}
            </p>
          </div>
          {/* Hidden hook for future user menu — appears on hover. No-op for now. */}
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 motion-reduce:transition-none"
          >
            <ChevronsUpDown size={14} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ────── Desktop Sidebar ────── */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 260 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className="hidden lg:flex flex-col bg-white dark:bg-neutral-950 border-r border-neutral-200/60 dark:border-neutral-800/80 shadow-[1px_0_0_0_rgba(16,24,40,0.02),4px_0_24px_-12px_rgba(16,24,40,0.06)] h-screen sticky top-0 transition-colors duration-200"
      >
        {/* Header — collapse toggle only. Brand mark removed because the
            top navigation bar already shows the Aktivity logo, and a second
            one in the sidebar duplicated the visual without adding info. */}
        <div className={`px-3 pt-4 pb-4 border-b border-neutral-100 dark:border-neutral-900 flex items-center ${collapsed ? 'justify-center' : 'justify-end'}`}>
          <button
            onClick={onToggle}
            className="w-7 h-7 rounded-lg border border-neutral-200/70 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900 hover:text-neutral-700 dark:hover:text-neutral-200 flex items-center justify-center transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft
              size={14}
              className="transition-transform duration-200 motion-reduce:transition-none"
              style={{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </button>
        </div>

        {/* Org Switcher (trainers with multiple orgs) — wrapped in a tinted
            chip container so it reads as "context", not another nav row. */}
        {user?.role === 'trainer' && (
          <div className="mx-3 mt-3 mb-1 rounded-xl bg-neutral-50 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-200/60 dark:ring-neutral-800 p-1">
            <OrgSwitcher user={user} collapsed={collapsed} />
          </div>
        )}

        {/* Nav list (sectioned) */}
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          <ul className="space-y-0.5">
            {renderNavList(false)}
          </ul>
        </nav>

        {/* User footer card */}
        <UserFooter />
      </motion.aside>

      {/* ────── Mobile Slide-out Drawer ────── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-neutral-950/50 backdrop-blur-md z-50"
              onClick={onMobileClose}
            />

            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-white dark:bg-neutral-950 z-50 shadow-[8px_0_32px_-8px_rgba(16,24,40,0.18)] flex flex-col border-r border-neutral-200/60 dark:border-neutral-800/80"
            >
              {/* Drawer header — brand mark dropped; topbar already has it */}
              <div className="flex items-center justify-between px-4 pt-4 pb-4 border-b border-neutral-100 dark:border-neutral-900">
                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Menu</span>
                <button
                  onClick={onMobileClose}
                  className="w-7 h-7 rounded-lg border border-neutral-200/70 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900 hover:text-neutral-700 dark:hover:text-neutral-200 flex items-center justify-center transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                  aria-label="Close menu"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Org Switcher (trainers with multiple orgs) */}
              {user?.role === 'trainer' && (
                <div className="mx-3 mt-3 mb-1 rounded-xl bg-neutral-50 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-200/60 dark:ring-neutral-800 p-1">
                  <OrgSwitcher user={user} collapsed={false} />
                </div>
              )}

              <nav className="flex-1 overflow-y-auto py-3 px-3">
                <ul className="space-y-0.5">
                  {renderNavList(true)}
                </ul>
              </nav>

              <UserFooter isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
